export const vertexShader = `#version 300 es
in vec2 position;
uniform vec2 u_resolution;
uniform vec2 u_camera;
uniform float u_zoom;
uniform vec2 u_positionOffset;
out vec2 v_worldPosition;

void main() {
  vec2 worldPosition = position + u_positionOffset;
  v_worldPosition = worldPosition;
  vec2 screen = (worldPosition - u_camera) * u_zoom;
  vec2 clip = (screen / u_resolution) * 2.0 - 1.0;
  gl_Position = vec4(clip * vec2(1.0, -1.0), 0.0, 1.0);
}
`;

export const textureVertexShader = `#version 300 es
in vec2 position;
in vec2 texcoord;
uniform vec2 u_resolution;
uniform vec2 u_camera;
uniform float u_zoom;
out vec2 v_texcoord;
out vec2 v_worldPosition;

void main() {
  v_texcoord = texcoord;
  v_worldPosition = position;
  vec2 screen = (position - u_camera) * u_zoom;
  vec2 clip = (screen / u_resolution) * 2.0 - 1.0;
  gl_Position = vec4(clip * vec2(1.0, -1.0), 0.0, 1.0);
}
`;

const clipUniformsAndHelpers = `
uniform int u_clipPointCount;
uniform vec2 u_clipPoints[64];

bool pointInsideClip(vec2 point) {
  if (u_clipPointCount < 3) {
    return true;
  }

  bool inside = false;
  vec2 previous = u_clipPoints[u_clipPointCount - 1];
  for (int index = 0; index < 64; index += 1) {
    if (index >= u_clipPointCount) {
      break;
    }

    vec2 current = u_clipPoints[index];
    float denominator = previous.y - current.y;
    bool crosses = ((current.y > point.y) != (previous.y > point.y)) &&
      (point.x < (previous.x - current.x) * (point.y - current.y) / denominator + current.x);
    if (crosses) {
      inside = !inside;
    }
    previous = current;
  }

  return inside;
}
`;

export const textureFragmentShader = `#version 300 es
precision highp float;
in vec2 v_texcoord;
in vec2 v_worldPosition;
uniform sampler2D u_texture;
uniform float u_opacity;
uniform float u_brightness;
uniform float u_contrast;
uniform float u_saturation;
${clipUniformsAndHelpers}
out vec4 outColor;

void main() {
  if (!pointInsideClip(v_worldPosition)) {
    discard;
  }

  vec4 color = texture(u_texture, v_texcoord);
  color.rgb *= u_brightness;
  color.rgb = (color.rgb - 0.5) * u_contrast + 0.5;
  float luma = dot(color.rgb, vec3(0.2126, 0.7152, 0.0722));
  color.rgb = mix(vec3(luma), color.rgb, u_saturation);
  float alpha = color.a * u_opacity;
  vec3 rgb = clamp(color.rgb, 0.0, 1.0);
  outColor = vec4(rgb, alpha);
}
`;

/** Fullscreen NDC quad for post-process blurs (no camera transform). */
export const blurVertexShader = `#version 300 es
in vec2 position;
in vec2 texcoord;
out vec2 v_texcoord;

void main() {
  v_texcoord = texcoord;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

/**
 * Separable Gaussian blur. `u_direction` is one texel step in UV space,
 * e.g. (1/width, 0) for horizontal and (0, 1/height) for vertical.
 * Weights/offsets are packed for linear-sample optimization (pair of taps).
 *
 * Samples outside [0,1] contribute transparent (matches Canvas2D filter edges).
 * Filtering runs in premultiplied space; `u_premultiplySrc` / `u_unpremultiplyDst`
 * convert at the first/last pass so intermediate FBOs stay premul.
 */
export const blurFragmentShader = `#version 300 es
precision highp float;
in vec2 v_texcoord;
uniform sampler2D u_texture;
uniform vec2 u_direction;
uniform float u_offsets[16];
uniform float u_weights[16];
uniform int u_tapCount;
uniform int u_premultiplySrc;
uniform int u_unpremultiplyDst;
out vec4 outColor;

vec4 sampleTex(vec2 uv) {
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    return vec4(0.0);
  }
  vec4 c = texture(u_texture, uv);
  if (u_premultiplySrc != 0) {
    return vec4(c.rgb * c.a, c.a);
  }
  return c;
}

void main() {
  vec4 color = sampleTex(v_texcoord) * u_weights[0];
  for (int i = 1; i < 16; i++) {
    if (i >= u_tapCount) {
      break;
    }
    vec2 offset = u_direction * u_offsets[i];
    color += sampleTex(v_texcoord + offset) * u_weights[i];
    color += sampleTex(v_texcoord - offset) * u_weights[i];
  }
  if (u_unpremultiplyDst != 0) {
    if (color.a > 1e-4) {
      outColor = vec4(color.rgb / color.a, color.a);
    } else {
      outColor = vec4(0.0);
    }
  } else {
    outColor = color;
  }
}
`;

export const fragmentShader = `#version 300 es
precision highp float;
in vec2 v_worldPosition;
uniform int u_paintType;
uniform vec4 u_color;
uniform vec2 u_gradientStart;
uniform vec2 u_gradientEnd;
uniform vec2 u_radialCenter;
uniform float u_radialRadius;
uniform int u_gradientStopCount;
uniform float u_gradientPositions[32];
uniform vec4 u_gradientColors[32];
${clipUniformsAndHelpers}
out vec4 outColor;

vec4 mixGradientStops(vec4 fromColor, vec4 toColor, float t) {
  vec4 fromPremul = vec4(fromColor.rgb * fromColor.a, fromColor.a);
  vec4 toPremul = vec4(toColor.rgb * toColor.a, toColor.a);
  vec4 mixed = mix(fromPremul, toPremul, t);
  if (mixed.a <= 0.0) {
    return vec4(0.0);
  }
  return vec4(mixed.rgb / mixed.a, mixed.a);
}

vec4 gradientColorAt(float position) {
  vec4 currentColor = u_gradientColors[0];

  for (int index = 1; index < 32; index += 1) {
    if (index >= u_gradientStopCount) {
      break;
    }

    float previousPosition = u_gradientPositions[index - 1];
    float nextPosition = u_gradientPositions[index];
    vec4 nextColor = u_gradientColors[index];
    if (position <= nextPosition) {
      float span = max(nextPosition - previousPosition, 0.000001);
      return mixGradientStops(currentColor, nextColor, clamp((position - previousPosition) / span, 0.0, 1.0));
    }

    currentColor = nextColor;
  }

  return currentColor;
}

vec4 linearGradientColor() {
  vec2 axis = u_gradientEnd - u_gradientStart;
  float axisLength = max(dot(axis, axis), 0.000001);
  float position = clamp(dot(v_worldPosition - u_gradientStart, axis) / axisLength, 0.0, 1.0);
  return gradientColorAt(position);
}

vec4 radialGradientColor() {
  float position = clamp(distance(v_worldPosition, u_radialCenter) / max(u_radialRadius, 0.000001), 0.0, 1.0);
  return gradientColorAt(position);
}

void main() {
  if (!pointInsideClip(v_worldPosition)) {
    discard;
  }

  outColor = u_paintType == 1 ? linearGradientColor() : u_paintType == 2 ? radialGradientColor() : u_color;
}
`;

export const ellipseFragmentShader = `#version 300 es
precision highp float;
in vec2 v_worldPosition;
uniform int u_paintType;
uniform vec4 u_color;
uniform vec4 u_bounds;
uniform vec2 u_gradientStart;
uniform vec2 u_gradientEnd;
uniform vec2 u_radialCenter;
uniform float u_radialRadius;
uniform int u_gradientStopCount;
uniform float u_gradientPositions[32];
uniform vec4 u_gradientColors[32];
${clipUniformsAndHelpers}
out vec4 outColor;

vec4 mixGradientStops(vec4 fromColor, vec4 toColor, float t) {
  vec4 fromPremul = vec4(fromColor.rgb * fromColor.a, fromColor.a);
  vec4 toPremul = vec4(toColor.rgb * toColor.a, toColor.a);
  vec4 mixed = mix(fromPremul, toPremul, t);
  if (mixed.a <= 0.0) {
    return vec4(0.0);
  }
  return vec4(mixed.rgb / mixed.a, mixed.a);
}

vec4 gradientColorAt(float position) {
  vec4 currentColor = u_gradientColors[0];

  for (int index = 1; index < 32; index += 1) {
    if (index >= u_gradientStopCount) {
      break;
    }

    float previousPosition = u_gradientPositions[index - 1];
    float nextPosition = u_gradientPositions[index];
    vec4 nextColor = u_gradientColors[index];
    if (position <= nextPosition) {
      float span = max(nextPosition - previousPosition, 0.000001);
      return mixGradientStops(currentColor, nextColor, clamp((position - previousPosition) / span, 0.0, 1.0));
    }

    currentColor = nextColor;
  }

  return currentColor;
}

vec4 linearGradientColor() {
  vec2 axis = u_gradientEnd - u_gradientStart;
  float axisLength = max(dot(axis, axis), 0.000001);
  float position = clamp(dot(v_worldPosition - u_gradientStart, axis) / axisLength, 0.0, 1.0);
  return gradientColorAt(position);
}

vec4 radialGradientColor() {
  float position = clamp(distance(v_worldPosition, u_radialCenter) / max(u_radialRadius, 0.000001), 0.0, 1.0);
  return gradientColorAt(position);
}

void main() {
  if (!pointInsideClip(v_worldPosition)) {
    discard;
  }

  vec2 center = u_bounds.xy + u_bounds.zw * 0.5;
  vec2 radius = max(u_bounds.zw * 0.5, vec2(0.0001));
  vec2 p = (v_worldPosition - center) / radius;
  float signedDistance = length(p) - 1.0;
  float aa = max(fwidth(signedDistance), 0.000001);
  float coverage = 1.0 - smoothstep(-aa, aa, signedDistance);

  if (coverage <= 0.0) {
    discard;
  }

  vec4 paintColor = u_paintType == 1 ? linearGradientColor() : u_paintType == 2 ? radialGradientColor() : u_color;
  outColor = vec4(paintColor.rgb, paintColor.a * coverage);
}
`;

export const roundedRectFragmentShader = `#version 300 es
precision highp float;
in vec2 v_worldPosition;
uniform int u_paintType;
uniform vec4 u_color;
uniform vec4 u_bounds;
uniform vec4 u_cornerRadii;
uniform vec2 u_gradientStart;
uniform vec2 u_gradientEnd;
uniform vec2 u_radialCenter;
uniform float u_radialRadius;
uniform int u_gradientStopCount;
uniform float u_gradientPositions[32];
uniform vec4 u_gradientColors[32];
${clipUniformsAndHelpers}
out vec4 outColor;

vec4 mixGradientStops(vec4 fromColor, vec4 toColor, float t) {
  vec4 fromPremul = vec4(fromColor.rgb * fromColor.a, fromColor.a);
  vec4 toPremul = vec4(toColor.rgb * toColor.a, toColor.a);
  vec4 mixed = mix(fromPremul, toPremul, t);
  if (mixed.a <= 0.0) {
    return vec4(0.0);
  }
  return vec4(mixed.rgb / mixed.a, mixed.a);
}

vec4 gradientColorAt(float position) {
  vec4 currentColor = u_gradientColors[0];

  for (int index = 1; index < 32; index += 1) {
    if (index >= u_gradientStopCount) {
      break;
    }

    float previousPosition = u_gradientPositions[index - 1];
    float nextPosition = u_gradientPositions[index];
    vec4 nextColor = u_gradientColors[index];
    if (position <= nextPosition) {
      float span = max(nextPosition - previousPosition, 0.000001);
      return mixGradientStops(currentColor, nextColor, clamp((position - previousPosition) / span, 0.0, 1.0));
    }

    currentColor = nextColor;
  }

  return currentColor;
}

vec4 linearGradientColor() {
  vec2 axis = u_gradientEnd - u_gradientStart;
  float axisLength = max(dot(axis, axis), 0.000001);
  float position = clamp(dot(v_worldPosition - u_gradientStart, axis) / axisLength, 0.0, 1.0);
  return gradientColorAt(position);
}

vec4 radialGradientColor() {
  float position = clamp(distance(v_worldPosition, u_radialCenter) / max(u_radialRadius, 0.000001), 0.0, 1.0);
  return gradientColorAt(position);
}

float selectedRadius(vec2 local) {
  bool right = local.x > 0.0;
  bool bottom = local.y > 0.0;
  if (!right && !bottom) {
    return u_cornerRadii.x;
  }
  if (right && !bottom) {
    return u_cornerRadii.y;
  }
  if (right && bottom) {
    return u_cornerRadii.z;
  }
  return u_cornerRadii.w;
}

void main() {
  if (!pointInsideClip(v_worldPosition)) {
    discard;
  }

  vec2 center = u_bounds.xy + u_bounds.zw * 0.5;
  vec2 halfSize = max(abs(u_bounds.zw) * 0.5, vec2(0.0001));
  vec2 local = v_worldPosition - center;
  float radius = min(selectedRadius(local), min(halfSize.x, halfSize.y));
  vec2 q = abs(local) - halfSize + vec2(radius);
  float signedDistance = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0) - radius;
  float aa = max(fwidth(signedDistance), 0.000001);
  float coverage = 1.0 - smoothstep(-aa, aa, signedDistance);

  if (coverage <= 0.0) {
    discard;
  }

  vec4 paintColor = u_paintType == 1 ? linearGradientColor() : u_paintType == 2 ? radialGradientColor() : u_color;
  outColor = vec4(paintColor.rgb, paintColor.a * coverage);
}
`;
